import jsPDF from 'jspdf';
import Image from 'next/image';
import { useRef } from 'react';
import html2canvas from 'html2canvas';
import { useSelector } from 'react-redux';
import useTranslation from 'next-translate/useTranslation';

import SplitNumbers from '@/components/ui/LinkNumbers/SplitNumbers';
import { Button } from '@/components/ui';
import { ArrowRight, InfoCircleRed } from '@/icons/index';
import { useEsimFlow } from '@/hooks/useEsimFlow';
import { selectors as orderSelectors } from '@/domains/esim/order';
import { scrollToTop } from '@/utils/scrollToTop';
import { EsimOrderStep, EsimSwapStep } from '@/enums/esim';

import QrBlock from '../../Qr/Qr';

import styles from '../Checkout.module.scss';

const InfoCard = ({ formikMain, activeTab, numberDetail, goToNextStep }) => {
  const { t } = useTranslation();
  const qrCodeRef = useRef(null);
  const { isSwapFlow } = useEsimFlow();
  const isSpecialMsisdn = numberDetail?.data?.msisdn?.startsWith('1');
  const { trackId, qrData, tariffPrice, selectedPlan, disabled } = useSelector(
    orderSelectors.selectOrder,
  );

  const getPhoneNumber = () => {
    if (isSwapFlow) {
      return `${formikMain.values.phoneNumberCode.name}${formikMain.values.phoneNumber}`;
    }
    if (numberDetail?.data) {
      const phone = `0${numberDetail.data.prefix}${numberDetail.data.msisdn}`;
      localStorage.setItem('tel', phone);
      return phone;
    }
    return '';
  };
  const totalAmount = () => {
    const numberPrice = Number(numberDetail?.data?.price) || 0;
    const additionalCharge = isSpecialMsisdn ? 7 : 0;
    const planCharge = selectedPlan ? Number(tariffPrice) || 0 : 0;

    return numberPrice + additionalCharge + planCharge;
  };

  const isFinalStep =
    activeTab === EsimOrderStep.FINAL ||
    (isSwapFlow && activeTab === EsimSwapStep.FINAL);

  const getQrCode = () => {
    if (isSwapFlow) {
      html2canvas(qrCodeRef.current).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();

        pdf.addImage('/assets/leftStar.png', 'png', 0, 0, 50, 90);
        pdf.addImage('/assets/rightStar.png', 'png', 170, 0, 40, 90);
        pdf.addImage('/assets/bottomStar.png', 'png', 0, 251, 80, 45);
        pdf.addImage('/assets/qrContact.png', 'png', 90, 275, 110, 10);
        pdf.addImage('/assets/qrLogo.png', 'png', 80, 20, 60, 12);
        pdf.addImage('/assets/qrButton.png', 'png', 63, 190, 97, 20);
        pdf.addImage(imgData, 'JPEG', 63, 80, 97, 97);
        pdf.setTextColor(255, 255, 255);
        pdf.text(
          `${`${formikMain?.values?.phoneNumberCode?.name} ${formikMain?.values?.phoneNumber}`}`,
          95,
          202,
        );
        pdf.setFillColor(255, 255, 255);

        pdf.save('qrCode.pdf');
      });
    } else {
      window.open(
        `https://esim.bakcell.com/api/contract-manager/contract/download?providerId=1&trackId=${trackId}&token=${qrData?.contractToken}`,
        '_blank',
      );
    }
  };
  const handleBtnClick = () => {
    scrollToTop();
    goToNextStep();
  };

  const renderPriceList = (numberDetail, selectedPlan, tariffPrice) => (
    <ul
      className={styles.list}
      itemScope
      itemType='https://schema.org/ItemList'
    >
      <li>
        <span className={styles.text}>{t('esim:checkout.card.esimNum')}</span>
        <span className={styles.value}>
          {isSpecialMsisdn
            ? Number(numberDetail?.data?.price) + 7
            : numberDetail?.data?.price ?? 0}
          AZN
        </span>
      </li>
      {!!selectedPlan && (
        <li>
          <span className={styles.text}>{t('esim:checkout.card.tariff')}</span>
          <span className={styles.value}>{tariffPrice} AZN</span>
        </li>
      )}
    </ul>
  );

  const renderTotal = () => (
    <div
      className={styles.bottom}
      itemScope
      itemType='https://schema.org/Thing'
    >
      <span className={styles.totalTitle}>
        {t('esim:checkout.card.total')}:
      </span>
      <span className={styles.total}>{isSwapFlow ? 0 : totalAmount()} AZN</span>
    </div>
  );

  const renderQRDetails = (qrData) => (
    <div
      className={styles.qr_detail_info}
      itemScope
      itemType='https://schema.org/Thing'
    >
      {qrData?.smdp && (
        <p className={styles.info}>
          <span className={styles.text_red}>SM-DP:</span> {qrData?.smdp}
        </p>
      )}
      {qrData?.activationCode && (
        <p className={styles.info}>
          <span className={styles.text_red}>
            {t('esim:common.activationCode')}:
          </span>
          {qrData?.activationCode}
        </p>
      )}
    </div>
  );

  return (
    <div
      className={styles.right}
      itemScope
      itemType='https://schema.org/Property'
    >
      <div className={styles.rightContent}>
        <strong className={styles.title}>
          {t('esim:checkout.card.title')}
        </strong>
        <div className={styles.number}>
          <span className={styles.text}>{t('esim:checkout.card.number')}:</span>
          <SplitNumbers
            className={styles.value}
            numbers={getPhoneNumber()}
            isNotLink
          />
        </div>

        {isFinalStep && renderQRDetails(qrData)}

        {isFinalStep && (
          <div className={styles.qrBlock}>
            <div className={styles.qr}>
              <QrBlock qrRef={qrCodeRef} {...qrData} />
            </div>
            <span className={styles.text}>{t('esim:common.content')}</span>
          </div>
        )}

        {isFinalStep ? (
          <>
            <Button
              variant='white'
              className={styles.download_qr_btn}
              onClick={getQrCode}
            >
              {isSwapFlow
                ? t('esim:common.donwloadQrCode')
                : t('esim:common.downloadContract')}
              <Image
                src='/assets/download.png'
                width={20}
                height={20}
                alt={t('esim:common.downloadContract')}
                title={t('esim:common.downloadContract')}
                className={styles.icon}
                loading='lazy'
              />
            </Button>
            {renderPriceList(numberDetail, selectedPlan, tariffPrice)}
            {renderTotal()}
            <div className={styles.banner}>
              <div>
                <InfoCircleRed />
              </div>
              <span className={styles.bannerText}>
                {t('esim:checkout.card.attention')}
              </span>
            </div>
          </>
        ) : (
          <>
            {renderPriceList(numberDetail, selectedPlan, tariffPrice)}
            {renderTotal()}
            <Button
              type='submit'
              disabled={
                !formikMain.isValid ||
                !formikMain.dirty ||
                disabled ||
                !formikMain.values.terms
              }
              className={styles.nextStep}
              onClick={handleBtnClick}
            >
              {t('esim:checkout.actions.continue')}
              <ArrowRight width={24} />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default InfoCard;
